import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Drawer,
  Group,
  Loader,
  Paper,
  ScrollArea,
  Select,
  Stack,
  Text,
  Textarea,
  Tooltip,
} from '@mantine/core';
import { IconBrain, IconPlus, IconSend, IconX } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';

import { apiFetch, errorMessage } from '@/api/client';

import { AiText } from './AiText';
import { AI_KEY, type AiChat, type AiConversation, type AiConversationDetail } from './api';
import type { AssistantPatient } from './context';
import { AiNotice, Feedback } from './Feedback';

const EXAMPLES = [
  'Comment enregistrer l’arrivée d’un patient ?',
  'Rédige une lettre d’orientation vers un cardiologue à partir de mes notes : …',
  'Quelles informations noter lors d’une consultation prénatale ?',
];

/** Assistant « GestClinic AI » : discussion, historique, avis. */
export function AssistantDrawer({
  opened,
  onClose,
  patient,
  onDetachPatient,
}: {
  opened: boolean;
  onClose: () => void;
  patient: AssistantPatient | null;
  onDetachPatient: () => void;
}) {
  const queryClient = useQueryClient();
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [message, setMessage] = useState('');
  const bottom = useRef<HTMLDivElement>(null);

  const conversations = useQuery({
    queryKey: [...AI_KEY, 'conversations'],
    queryFn: () => apiFetch<AiConversation[]>('/assistant/conversations'),
    enabled: opened,
  });
  const detail = useQuery({
    queryKey: [...AI_KEY, 'conversation', conversationId],
    queryFn: () => apiFetch<AiConversationDetail>(`/assistant/conversations/${conversationId}`),
    enabled: opened && Boolean(conversationId),
  });
  const send = useMutation({
    mutationFn: (text: string) =>
      apiFetch<AiChat>('/assistant/chat', {
        method: 'POST',
        body: {
          message: text,
          conversation: conversationId,
          patient: conversationId ? null : (patient?.id ?? null),
        },
      }),
    onSuccess: (data) => {
      setMessage('');
      setConversationId(data.conversation.id);
      queryClient.setQueryData(
        [...AI_KEY, 'conversation', data.conversation.id],
        data.conversation,
      );
      void queryClient.invalidateQueries({ queryKey: [...AI_KEY, 'conversations'] });
    },
  });

  useEffect(() => {
    bottom.current?.scrollIntoView({ block: 'end' });
  }, [detail.data, send.isPending]);

  const submit = () => {
    const text = message.trim();
    if (text && !send.isPending) send.mutate(text);
  };
  const messages = conversationId ? (detail.data?.messages ?? []) : [];

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size="lg"
      title={
        <Group gap="xs">
          <IconBrain size={20} color="var(--mantine-color-violet-6)" />
          <Text fw={700}>GestClinic AI</Text>
        </Group>
      }
    >
      <Stack h="calc(100dvh - 90px)" gap="sm">
        <Group gap="xs" wrap="nowrap">
          <Select
            placeholder="Discussions précédentes"
            data={(conversations.data ?? []).map((c) => ({
              value: c.id,
              label: c.title || 'Discussion',
            }))}
            value={conversationId}
            onChange={setConversationId}
            clearable
            searchable
            style={{ flex: 1 }}
          />
          <Tooltip label="Nouvelle discussion">
            <ActionIcon
              variant="default"
              size="lg"
              aria-label="Nouvelle discussion"
              onClick={() => setConversationId(null)}
            >
              <IconPlus size={16} />
            </ActionIcon>
          </Tooltip>
        </Group>
        {patient && !conversationId && (
          <Group gap="xs">
            <Badge color="violet" variant="light">
              Dossier joint : {patient.label}
            </Badge>
            <ActionIcon
              size="xs"
              variant="subtle"
              aria-label="Retirer le dossier"
              onClick={onDetachPatient}
            >
              <IconX size={12} />
            </ActionIcon>
          </Group>
        )}
        <ScrollArea style={{ flex: 1 }} type="auto">
          <Stack gap="sm" pr="xs">
            {messages.length === 0 && !send.isPending && (
              <Paper withBorder p="sm" bg="var(--mantine-color-violet-light)">
                <Text size="sm" fw={600}>
                  Posez une question ou demandez une rédaction.
                </Text>
                <Text size="xs" c="dimmed" mb={6}>
                  L’assistant n’utilise que les données auxquelles vous avez accès. Il vous aide, il
                  ne décide pas.
                </Text>
                <Stack gap={4}>
                  {EXAMPLES.map((ex) => (
                    <Text
                      key={ex}
                      size="xs"
                      c="violet.8"
                      style={{ cursor: 'pointer' }}
                      onClick={() => setMessage(ex)}
                    >
                      « {ex} »
                    </Text>
                  ))}
                </Stack>
              </Paper>
            )}
            {messages.map((m) =>
              m.role === 'user' ? (
                <Paper key={m.id} p="xs" radius="md" bg="var(--mantine-color-gray-1)" ml="xl">
                  <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
                    {m.content}
                  </Text>
                </Paper>
              ) : (
                <Paper key={m.id} p="sm" radius="md" withBorder mr="md">
                  <AiText text={m.content} />
                  {m.request_id && (
                    <Group justify="flex-end" mt={4}>
                      <Feedback requestId={m.request_id} />
                    </Group>
                  )}
                </Paper>
              ),
            )}
            {send.isPending && (
              <Group gap="xs">
                <Loader size="xs" color="violet" />
                <Text size="sm" c="dimmed">
                  L’assistant rédige sa réponse…
                </Text>
              </Group>
            )}
            {send.isError && <Alert color="orange">{errorMessage(send.error)}</Alert>}
            <div ref={bottom} />
          </Stack>
        </ScrollArea>
        <Textarea
          placeholder="Votre question (Ctrl + Entrée pour envoyer)"
          autosize
          minRows={2}
          maxRows={6}
          value={message}
          onChange={(e) => setMessage(e.currentTarget.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) submit();
          }}
        />
        <Group justify="space-between">
          <AiNotice>Réponses générées par l’IA : à vérifier avant usage.</AiNotice>
          <Button
            color="violet"
            leftSection={<IconSend size={16} />}
            loading={send.isPending}
            disabled={!message.trim()}
            onClick={submit}
          >
            Envoyer
          </Button>
        </Group>
      </Stack>
    </Drawer>
  );
}
